"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";

import { NAV_SEARCH_OPEN_EVENT } from "@/lib/navigation/nav-search-open";
import { canSearchAdminNavigation } from "@/lib/agent-tools/navigation-search-access";
import { isHrefVisible } from "@/lib/plugins";
import { isSearchShortcut } from "@/lib/keyboard";

const QUICK_LINKS = [
  {
    id: "quick-home",
    group: "Quick links",
    kind: "page",
    badge: "Page",
    title: "Home",
    subtitle: "Today, priorities, and coaching",
    href: "/",
  },
  {
    id: "quick-learning-paths",
    group: "Quick links",
    kind: "learning_path",
    badge: "Page",
    title: "Learning paths",
    subtitle: "Assigned paths and progress",
    href: "/dashboard/learning-paths",
  },
  {
    id: "quick-tests",
    group: "Quick links",
    kind: "test",
    badge: "Page",
    title: "Tests",
    subtitle: "Knowledge checks and results",
    href: "/dashboard/tests",
  },
];

/** A page a shortcut asked for, shaped like the index's own page results. */
function toPinnedResult(page) {
  return {
    id: `page-${page.href}`,
    group: "Pages",
    kind: "page",
    badge: "Page",
    title: page.title,
    subtitle: page.subtitle,
    href: page.href,
    shortcut: page.shortcut,
  };
}

/**
 * Bucket results by group while keeping the server's relevance order: a group
 * appears as high as its best result does. A fixed group order would bury the
 * closest match — typing a page's name would still list assignments first.
 */
function groupResults(results) {
  const grouped = new Map();

  results.forEach((result, index) => {
    const group = result.group || "Results";
    if (!grouped.has(group)) grouped.set(group, { bestIndex: index, items: [] });
    grouped.get(group).items.push(result);
  });

  return Array.from(grouped.entries())
    .sort(([, a], [, b]) => a.bestIndex - b.bestIndex)
    .map(([group, { items }]) => [group, items]);
}

/**
 * The ⌘K search: its open state, the query, the results and what opening and
 * choosing do. One instance lives in the side nav and feeds whichever surface
 * the screen size calls for — the pane beside the icon rail, or the dialog
 * over a small screen. Opening comes from the shortcut, the trigger button,
 * or the `openNavSearch` window event (the mobile header, a page shortcut, a
 * keystroke at a nav menu).
 */
export function useNavSearch({ triggerRef } = {}) {
  const router = useRouter();
  const { data: session } = useSession();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  // Set when the search was opened from a nav dropdown or a page shortcut: the
  // user was reading a list of destinations, so destinations lead the results.
  // It holds for the whole open, even once the query moves off the pinned
  // page, and is cleared on close.
  const [pagesFirst, setPagesFirst] = useState(false);
  // Which menu that was. Sent to the server, where it breaks ties between pages
  // that share a name across sections — nothing more.
  const [preferredSection, setPreferredSection] = useState("");
  // The page a shortcut opened search for. It leads the list, highlighted,
  // until the query is edited away from its name.
  const [pinnedPage, setPinnedPage] = useState(null);
  // Guards one-event-per-session logging (see the commit effect below).
  const committedRef = useRef(false);
  // True when this open was seeded with a keystroke from a nav menu. That
  // character is already in the input, so the caret belongs after it rather
  // than over it (see focusInput).
  const seededRef = useRef(false);
  // Set by `dismiss`: Escape or the field's back arrow closed the search, so
  // focus goes back to the trigger (`triggerRef`) once it is on screen again.
  // Choosing a result leaves focus alone; the page it opened takes over.
  const returnFocusRef = useRef(false);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (isSearchShortcut(event)) {
        event.preventDefault();
        setOpen((value) => !value);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Handoff from elsewhere: the mobile header's button, a page shortcut, or
  // typing at the rail or its panel (useNavTypeToSearch), carrying the
  // keystroke that started it.
  useEffect(() => {
    const onOpenRequest = (event) => {
      const detail = event.detail || {};
      setPagesFirst(Boolean(detail.pagesFirst));
      setPreferredSection(detail.section || "");
      setPinnedPage(detail.page?.href ? toPinnedResult(detail.page) : null);
      setQuery(detail.query || "");
      seededRef.current = Boolean(detail.query);
      setOpen(true);
    };

    window.addEventListener(NAV_SEARCH_OPEN_EVENT, onOpenRequest);
    return () =>
      window.removeEventListener(NAV_SEARCH_OPEN_EVENT, onOpenRequest);
  }, []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const controller = new AbortController();
    const trimmedQuery = query.trim();
    const searchParams = new URLSearchParams({
      // Send what was debounced on, so a stray leading space can't produce a
      // different request than the one the debounce decided about.
      q: trimmedQuery,
      limit: "5",
      ...(preferredSection ? { section: preferredSection } : {}),
    });

    setIsLoading(true);
    setError("");
    setResults([]);

    const timeout = window.setTimeout(async () => {
      try {
        const response = await fetch(`/api/nav-search?${searchParams}`, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error("Search failed");
        }

        const payload = await response.json();
        setResults(Array.isArray(payload.results) ? payload.results : []);
      } catch (fetchError) {
        if (fetchError.name !== "AbortError") {
          setError("Search is unavailable right now.");
          setResults([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    }, trimmedQuery ? 180 : 0);

    return () => {
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, [open, preferredSection, query]);

  const displayResults = useMemo(() => {
    // Search results are already plugin-filtered server-side; QUICK_LINKS is a
    // hardcoded list here, so it gets the same check rather than becoming the
    // one place a disabled plugin can still be reached (SAL-2912).
    const quickLinks = QUICK_LINKS.filter((link) =>
      isHrefVisible(session?.workspace, link.href),
    );
    const pinned =
      pinnedPage && query.trim() === pinnedPage.title ? pinnedPage : null;
    const found = pinned
      ? [pinned, ...results.filter((result) => result.id !== pinned.id)]
      : results;
    const base = query.trim() ? found : [...found, ...quickLinks];
    if (!pagesFirst) return base;

    // Stable partition rather than a re-sort: page links move ahead as a block
    // and everything keeps the relevance order the server decided on. Because
    // groupResults ranks a group by its best result, this alone puts the
    // "Pages" heading at the top of the list.
    return [
      ...base.filter((result) => result.kind === "page"),
      ...base.filter((result) => result.kind !== "page"),
    ];
  }, [pagesFirst, pinnedPage, query, results, session?.workspace]);

  const groupedResults = useMemo(
    () => groupResults(displayResults),
    [displayResults],
  );

  // Logs the final search of a session — the page the user was on, the result
  // count, and the opened result (if any). Only the settled query is saved, not
  // each keystroke. Guarded so close + select don't double-log.
  const commit = (selectedResult) => {
    if (committedRef.current) return;
    const trimmed = query.trim();
    if (!trimmed) return;
    committedRef.current = true;

    const payload = {
      query: trimmed,
      resultCount: results.length,
      pagePath:
        typeof window !== "undefined"
          ? `${window.location.pathname}${window.location.search}`
          : undefined,
      selectedResult: selectedResult?.id
        ? {
            id: selectedResult.id,
            title: selectedResult.title,
            href: selectedResult.href,
            kind: selectedResult.kind,
            group: selectedResult.group,
            badge: selectedResult.badge,
          }
        : null,
    };

    void fetch("/api/nav-search-events", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      /* non-blocking analytics */
    });
  };

  // One event per search session: opening arms the guard; any close path
  // (Esc, outside-click, ⌘K) records the final typed query. Selecting a result
  // commits early in select, so this close commit is then a no-op.
  useEffect(() => {
    if (open) {
      committedRef.current = false;
    } else {
      commit(null);
      // The next open is a plain ⌘K until something asks for pages first again.
      setPagesFirst(false);
      setPreferredSection("");
      setPinnedPage(null);
      seededRef.current = false;
      if (returnFocusRef.current) {
        returnFocusRef.current = false;
        triggerRef?.current?.focus();
      }
    }
    // commit intentionally reads the latest query/results via closure on the
    // render where `open` flips to false.
  }, [open]);

  /**
   * Focus the search input as the surface opens. A plain ⌘K open selects the
   * stale query from last time, so typing replaces it. When a nav menu handed
   * off the keystroke that opened this, that character is the query so far,
   * and selecting it would make the next key overwrite it: the caret goes
   * after the seed instead.
   */
  const focusInput = (input) => {
    if (!input) return;
    input.focus({ preventScroll: true });
    if (seededRef.current) {
      const end = input.value.length;
      input.setSelectionRange(end, end);
    } else {
      input.select();
    }
  };

  /** Close from the keyboard or the field's back arrow: focus returns to the trigger. */
  const dismiss = () => {
    returnFocusRef.current = true;
    setOpen(false);
  };

  const select = (result) => {
    const href = result?.href;
    if (!href) return;
    commit(result);
    setOpen(false);
    setQuery("");
    router.push(href);
  };

  const placeholder = canSearchAdminNavigation(session?.user)
    ? "Search pages, assignments, Q&As, people..."
    : pagesFirst
      ? "Search pages, assignments, resources..."
      : "Search assignments, learning paths, resources...";

  return {
    open,
    setOpen,
    close: () => setOpen(false),
    dismiss,
    toggle: () => setOpen((value) => !value),
    query,
    setQuery,
    groupedResults,
    hasResults: results.length > 0,
    isLoading,
    error,
    placeholder,
    select,
    focusInput,
  };
}
